import React, { useEffect, useState } from "react";
import { DialogContent, Grid } from "@mui/material";
import { AxiosError } from "axios";

import { StandardDialogFooter } from "../../../../../../pages/components/dialogs/standard-dialog/standard-dialog";
import { mapValidationErrors } from "../../../../../../pages/user/settings/personal-info/personal-info.service";
import { CategoryModel } from "../../../../../../pages/shared/models/CategoryModel";
import {
  Message,
  MessageSections,
} from "../../../../../../pages/components/message/message";
import { BarekamAttachment } from "../../../../../../pages/components/attachment/attachment";
import { OnlineStoresModel } from "../../../../../../pages/shared/models/onlineStoresModel";
import { BarekamMenuItem } from "../../../../../../pages/components/select/styled/orders";
import BarekamInput from "../../../../../../pages/components/input/barekam-input";
import { BarekamSelect } from "../../../../../../pages/components/select/select";
import { getMessage } from "../../../../../../pages/shared/helper-functions";
import BarekamCheckbox from "../../../../../../pages/components/checkbox";
import BarekamText from "../../../../../../pages/components/text";

import { useAppDispatch, useAppSelector } from "../../../../../../redux/store";
import onlineStoreService from "../../../../../../services/online-stores-service";
import onlineStoreCategoryService from "../../../../../../services/online-story-category-service";

import { getCountries } from "../../../../../../redux/slices/static-data.slice";
import { CountryModel } from "../../../../../../pages/shared/models/countryModel";

const EditOnlineStores = ({
  onlineStores,
  onCancel,
}: {
  onlineStores: OnlineStoresModel;
  onCancel: () => void;
}) => {
  const [modifiedStores, setModifiedStores] = useState({
    featured: false,
    ...onlineStores,
  });
  const [storeCategories, setStoreCategories] = useState<
    CategoryModel[] | []
  >();
  const [file, setFile] = useState<File>();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const dispatch = useAppDispatch();
  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );
  const { countries } = useAppSelector((state) => state.staticDataReducer);
  const [isChecked, setIsChecked] = useState(onlineStores.featured);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response =
          await onlineStoreCategoryService.getOnlineStoreCategories(
            undefined,
            20,
            0,
          );
        setStoreCategories(response.data);
      } catch (err) {
        console.log("Failed to fetch categories.");
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    dispatch(getCountries(true));
  }, []);

  function handleChange(
    field:
      | "name"
      | "description"
      | "url"
      | "image"
      | "featured"
      | "categoryIds"
      | "country",
    value: any,
  ) {
    if (field === 'featured') {
      setIsChecked(!isChecked);
    }
    if (field === 'categoryIds') {
      setModifiedStores((prevState) => {
        return { ...prevState, [field]: [value] };
      });
      return;
    }
    setModifiedStores((prevState) => {
      return { ...prevState, [field]: value };
    });
  }

  function validateData() {
    const errors = {} as { [key: string]: string };
    const requiredFields = ["url", "name", "country"];

    for (const field of requiredFields) {
      const key = field as keyof typeof modifiedStores;
      if (
        modifiedStores[key] === undefined ||
        modifiedStores[key] === null ||
        modifiedStores[key] === ""
      ) {
        errors[key] = "required";
      }
    }
    console.log("errors", errors);
    return errors;
  }

  function handleSave() {
    const errors = validateData();
    setErrors(errors);

    if (Object.keys(errors).length === 0) {
      const formData = new FormData();
      if (file?.name) {
        formData.append("file", file);
      }
      formData.append("data", JSON.stringify(modifiedStores));

      if (onlineStores.id) {
        onlineStoreService
          .editStore(onlineStores.id, formData)
          .then(() => onCancel())
          .catch((reason) => {
            if (reason instanceof AxiosError) {
              setErrors(mapValidationErrors(reason));
            }
          });
      } else {
        onlineStoreService
          .createStore(formData)
          .then(() => onCancel())
          .catch((reason) => {
            if (reason instanceof AxiosError) {
              setErrors(mapValidationErrors(reason));
            }
          });
      }
    }
  }

  return (
    <>
      <DialogContent>
        <Grid container rowSpacing={"1rem"} columnSpacing={"1rem"}>
          <Grid item xs={12}>
            <BarekamInput
              value={modifiedStores.name}
              key={"name"}
              placeholder={getMessage(
                "name",
                MessageSections.MAIN,
                messageCache,
                languageId,
              )}
              customInputVariant={"standard"}
              width={"100%"}
              error={!!errors["name"]}
              errorMessage={
                errors["name"] &&
                getMessage(
                  errors["name"],
                  MessageSections.MAIN,
                  messageCache,
                  languageId,
                )
              }
              onChange={(event) => handleChange("name", event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <BarekamInput
              value={modifiedStores.url}
              key={"url"}
              placeholder={getMessage(
                "url",
                MessageSections.MAIN,
                messageCache,
                languageId,
              )}
              customInputVariant={"standard"}
              width={"100%"}
              error={!!errors["url"]}
              errorMessage={
                errors["url"] &&
                getMessage(
                  errors["url"],
                  MessageSections.MAIN,
                  messageCache,
                  languageId,
                )
              }
              onChange={(event) => handleChange("url", event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <BarekamSelect
              variant={"outlined"}
              label={
                <BarekamText variant={"1rem"}>
                  <Message
                    messageKey={"country"}
                    sectionId={MessageSections.ORDERS}
                  />
                </BarekamText>
              }
              value={modifiedStores?.country || ""}
              type={"standard"}
              error={!!errors["country"]}
              errorMessage={
                errors["country"] &&
                getMessage(
                  errors["country"],
                  MessageSections.ORDERS,
                  messageCache,
                  languageId,
                )
              }
              fullWidth
              required
            >
              {countries?.map((country: CountryModel) => {
                return (
                  <BarekamMenuItem
                    key={country.countryId}
                    value={country.countryId}
                    onClick={() => handleChange("country", country.countryId)}
                  >
                    {country.name[languageId]}
                  </BarekamMenuItem>
                );
              })}
            </BarekamSelect>
          </Grid>
          <Grid item xs={12}>
            <BarekamSelect
              variant={"outlined"}
              label={
                <BarekamText variant={"1rem"}>
                  <Message
                    messageKey={"category"}
                    sectionId={MessageSections.ORDERS}
                  />
                </BarekamText>
              }
              value={modifiedStores?.categoryIds || ""}
              type={"standard"}
              error={!!errors["categoryIds"]}
              errorMessage={
                errors["categoryIds"] &&
                getMessage(
                  errors["categoryIds"],
                  MessageSections.ORDERS,
                  messageCache,
                  languageId,
                )
              }
              fullWidth
              required
            >
              {storeCategories?.map((list: CategoryModel) => {
                return (
                  <BarekamMenuItem
                    key={list.id}
                    value={list.id}
                    onClick={() => handleChange("categoryIds", list.id)}
                  >
                    {list.name[languageId]}
                  </BarekamMenuItem>
                );
              })}
            </BarekamSelect>
          </Grid>
          <Grid item xs={12}>
            <BarekamAttachment
              titleLabel={
                <Message
                  messageKey={"image"}
                  sectionId={MessageSections.MAIN}
                />
              }
              value={file}
              isSingle={true}
              onValueChange={(value: File) => setFile(value)}
            />
          </Grid>
          <Grid item xs={12}>
            <img src={onlineStores?.imgUrl} width={200} height={100} />
          </Grid>
          <Grid item xs={12}>
          <BarekamCheckbox
              checked={isChecked}
              onChange={(event) => handleChange("featured", event.target.checked)}
            />
            <BarekamText variant={"m"} opaque>
              <Message
                messageKey={"featured"}
                sectionId={MessageSections.MAIN}
              />
            </BarekamText>
          </Grid>
        </Grid>
      </DialogContent>

      <StandardDialogFooter
        onCancel={() => onCancel()}
        onSave={() => handleSave()}
        saveLabel={
          <Message messageKey={"save"} sectionId={MessageSections.MAIN} />
        }
      />
    </>
  );
};

export default EditOnlineStores;
